// Screenshot attachments for Help ▸ Report an Issue (FeedbackAttachments,
// db/06). Pure helpers first (unit-tested), then the two browser-only
// functions that read a File / clipboard into a downscaled data URL.
//
// Limits mirror sp_SubmitFeedback's own caps — the proc keeps only the first
// MAX_FEEDBACK_ATTACHMENTS image data URLs and refuses one over ~4M chars —
// so the browser refuses the same things up front with a readable reason
// instead of silently losing an image server-side.

export const MAX_FEEDBACK_ATTACHMENTS = 3;
export const MAX_IMAGE_DIMENSION_PX = 1600;    // longest side after downscale
export const JPEG_QUALITY = 0.85;
export const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024; // decoded bytes AFTER downscale; the proc's 4M-char cap is ~3 MB
export const MAX_SOURCE_BYTES = 25 * 1024 * 1024;    // refuse a monster file before even decoding it
export const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif", "image/bmp"];

export function isAcceptedImageType(type) {
  return ACCEPTED_IMAGE_TYPES.includes(String(type || "").toLowerCase());
}

// Decide which of the newly chosen files can be added given how many are
// already attached. Returns { accepted: File[], rejected: [{ name, reason }] }.
// Never throws; a file is rejected with a reason the form shows verbatim.
export function selectAttachmentFiles(files, existingCount = 0, limits = {}) {
  const max = limits.maxFiles ?? MAX_FEEDBACK_ATTACHMENTS;
  const maxSource = limits.maxSourceBytes ?? MAX_SOURCE_BYTES;
  const list = Array.from(files || []);
  const accepted = [];
  const rejected = [];
  let room = Math.max(0, max - (existingCount || 0));
  for (const f of list) {
    const name = f?.name || "image";
    if (!isAcceptedImageType(f?.type)) { rejected.push({ name, reason: "Only PNG, JPEG, WebP, GIF or BMP images can be attached." }); continue; }
    if ((f?.size || 0) > maxSource) { rejected.push({ name, reason: `Larger than ${Math.round(maxSource / 1024 / 1024)} MB.` }); continue; }
    if (room <= 0) { rejected.push({ name, reason: `Up to ${max} screenshots per submission.` }); continue; }
    accepted.push(f);
    room -= 1;
  }
  return { accepted, rejected };
}

// Approximate decoded byte length of a base64 data URL (what SizeBytes stores).
export function dataUrlBytes(dataUrl) {
  const s = String(dataUrl || "");
  const comma = s.indexOf(",");
  if (comma < 0) return 0;
  const b64 = s.slice(comma + 1);
  const padding = b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor(b64.length * 3 / 4) - padding);
}

export function contentTypeOfDataUrl(dataUrl) {
  const m = String(dataUrl || "").match(/^data:([^;,]+)[;,]/);
  return m ? m[1].toLowerCase() : null;
}

export function isImageDataUrl(dataUrl) {
  return /^data:image\/[a-z0-9.+-]+;base64,/i.test(String(dataUrl || ""));
}

// The JSON array sp_SubmitFeedback's @attachments parameter expects
// ({ file_name, content_type, data_url, size_bytes }, array order = display
// order). Drops anything that is not an image data URL so a bad entry can't
// make the whole submission fail.
export function attachmentsPayload(attachments = []) {
  return attachments
    .filter(a => isImageDataUrl(a?.dataUrl))
    .slice(0, MAX_FEEDBACK_ATTACHMENTS)
    .map(a => ({
      file_name: String(a.fileName || "screenshot").slice(0, 500),
      content_type: a.contentType || contentTypeOfDataUrl(a.dataUrl) || "image/jpeg",
      data_url: a.dataUrl,
      size_bytes: Number.isFinite(a.sizeBytes) ? a.sizeBytes : dataUrlBytes(a.dataUrl),
    }));
}

// Size a w×h image so its longest side is ≤ maxDim (never upscales).
export function fitDimensions(width, height, maxDim = MAX_IMAGE_DIMENSION_PX) {
  const w = Math.max(1, Math.round(width || 1));
  const h = Math.max(1, Math.round(height || 1));
  const longest = Math.max(w, h);
  if (longest <= maxDim) return { width: w, height: h, scaled: false };
  const k = maxDim / longest;
  return { width: Math.max(1, Math.round(w * k)), height: Math.max(1, Math.round(h * k)), scaled: true };
}

// Image files on a paste event (a screenshot on the clipboard arrives as a
// File named "image.png" in clipboardData.files / .items).
export function imageFilesFromClipboard(clipboardData) {
  const out = [];
  const files = clipboardData?.files ? Array.from(clipboardData.files) : [];
  for (const f of files) if (isAcceptedImageType(f.type)) out.push(f);
  if (!out.length && clipboardData?.items) {
    for (const it of Array.from(clipboardData.items)) {
      if (it.kind === "file" && isAcceptedImageType(it.type)) {
        const f = it.getAsFile?.();
        if (f) out.push(f);
      }
    }
  }
  return out;
}

// ── Browser-only ─────────────────────────────────────────────────────────────

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = () => reject(new Error(`Could not read ${file?.name || "file"}`));
    r.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Not a readable image"));
    img.src = src;
  });
}

// File → { fileName, contentType, dataUrl, sizeBytes, width, height, scaled }.
// Downscales to ≤ maxDim on the longest side and re-encodes as JPEG (a GIF's
// animation is lost — a screenshot never has one). Falls back to the original
// data URL when the canvas path fails (no DOM, decode error) as long as the
// original is under the byte cap; otherwise throws with a readable message.
export async function fileToAttachment(file, { maxDim = MAX_IMAGE_DIMENSION_PX, quality = JPEG_QUALITY, maxBytes = MAX_ATTACHMENT_BYTES } = {}) {
  const original = await readFileAsDataUrl(file);
  let dataUrl = original;
  let width = null, height = null, scaled = false;
  try {
    const img = await loadImage(original);
    const fit = fitDimensions(img.naturalWidth || img.width, img.naturalHeight || img.height, maxDim);
    width = fit.width; height = fit.height; scaled = fit.scaled;
    const needsReencode = fit.scaled || file.type !== "image/jpeg" || dataUrlBytes(original) > maxBytes;
    if (needsReencode && typeof document !== "undefined") {
      const canvas = document.createElement("canvas");
      canvas.width = fit.width; canvas.height = fit.height;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; // a PNG's transparency would come out black in JPEG
      ctx.fillRect(0, 0, fit.width, fit.height);
      ctx.drawImage(img, 0, 0, fit.width, fit.height);
      let q = quality;
      let out = canvas.toDataURL("image/jpeg", q);
      // Step quality down until under the cap (a dense 1600px screenshot at
      // 0.85 is typically 300–600 KB, so this rarely loops).
      while (dataUrlBytes(out) > maxBytes && q > 0.4) { q -= 0.15; out = canvas.toDataURL("image/jpeg", q); }
      dataUrl = out;
    }
  } catch {
    // fall through with the original data URL
  }
  const sizeBytes = dataUrlBytes(dataUrl);
  if (sizeBytes > maxBytes) throw new Error(`${file?.name || "Image"} is still over ${Math.round(maxBytes / 1024 / 1024)} MB after downscaling.`);
  if (!isImageDataUrl(dataUrl)) throw new Error(`${file?.name || "File"} is not an image.`);
  return { fileName: file?.name || "screenshot.jpg", contentType: contentTypeOfDataUrl(dataUrl), dataUrl, sizeBytes, width, height, scaled };
}
